import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLanguage();
  const [siteContent, setSiteContent] = useState({});
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => setSiteContent(r.data)).catch(() => {});
    axios.post(`${API}/analytics/visit`, { page: '/contact' }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true); setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message');
    } finally { setSending(false); }
  };

  const hours = siteContent.opening_hours || {};

  return (
    <div data-testid="contact-page">
      <section className="py-24 sm:py-32 bg-secondary/50">
        <div className="container mx-auto px-6 md:px-8 text-center">
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-3">{t('contact.title')}</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">{t('contact.title')}</h1>
          <p className="text-base text-muted-foreground mt-4 max-w-xl mx-auto">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="container mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="rounded-2xl border-border" data-testid="contact-form-card">
                <CardContent className="p-8">
                  {sent ? (
                    <div className="text-center py-12 animate-fade-in" data-testid="contact-success">
                      <CheckCircle className="w-16 h-16 text-primary mx-auto" />
                      <h3 className="font-heading text-2xl font-medium mt-4">{t('contact.success')}</h3>
                      <p className="text-muted-foreground mt-2">We'll get back to you as soon as possible.</p>
                      <Button className="mt-6 rounded-full" onClick={() => setSent(false)} data-testid="send-another-btn">Send Another Message</Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="name" className="text-sm font-medium">{t('contact.name')}</Label>
                          <Input id="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="h-12 mt-2 rounded-xl" data-testid="contact-name" />
                        </div>
                        <div>
                          <Label htmlFor="email" className="text-sm font-medium">{t('contact.email')}</Label>
                          <Input id="email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="h-12 mt-2 rounded-xl" data-testid="contact-email" />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-sm font-medium">{t('contact.phone')}</Label>
                        <Input id="phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="h-12 mt-2 rounded-xl" data-testid="contact-phone" />
                      </div>
                      <div>
                        <Label htmlFor="message" className="text-sm font-medium">{t('contact.message')}</Label>
                        <Textarea id="message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required rows={5} className="mt-2 rounded-xl" data-testid="contact-message" />
                      </div>
                      {error && <p className="text-sm text-destructive" data-testid="contact-error">{error}</p>}
                      <Button type="submit" disabled={sending} className="rounded-full px-8" data-testid="contact-submit">
                        {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> {t('contact.send')}</>}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Info */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="rounded-2xl border-border" data-testid="contact-info-card">
                <CardContent className="p-8 space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading font-medium">{t('location.address')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{siteContent.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading font-medium">{t('location.phone')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{siteContent.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading font-medium">{t('location.email')}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{siteContent.email}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="rounded-2xl border-border" data-testid="contact-hours-card">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-5 h-5 text-primary" />
                    <h4 className="font-heading font-medium">{t('location.hours')}</h4>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(hours).map(([day, time]) => (
                      <div key={day} className="flex justify-between text-sm">
                        <span className="font-medium">{day}</span>
                        <span className={time === 'Closed' ? 'text-destructive' : 'text-muted-foreground'}>{time}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
